import { HttpBackend, HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthEndpoints } from '@medicalapp/shared-core';
import { APP_CONFIG_SERVICE } from '@core/services/common/app-config.service';
import { CurrentUserService } from '@core/services/common/current-user.service';
import { ApiResponse } from '@models/response.model';
import { ICurrentUserModel, ILoginResponseModel, IResetPasswordModel, IChangePasswordModel } from '@models/auth/auth.model';
import { IAppConfig } from '@models/app-config/app.config.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly httpClient: HttpClient;
  private readonly currentUserService = inject(CurrentUserService);
  private readonly appConfig: IAppConfig = inject(APP_CONFIG_SERVICE);
  private readonly httpWithInterceptors = inject(HttpClient);

  constructor(handler: HttpBackend) {
    this.httpClient = new HttpClient(handler);
  }

  login(credentials: any): Observable<HttpResponse<ApiResponse<ILoginResponseModel>>> {
    return this.httpClient.post<ApiResponse<ILoginResponseModel>>(
      `${this.appConfig.apiEndpoint}${AuthEndpoints.Login}`,
      credentials,
      { observe: 'response' }
    );
  }

  logout(): Observable<ApiResponse<any>> {
    const currentUser: ICurrentUserModel | null = this.currentUserService.currentUser();
    return this.httpWithInterceptors
      .post<ApiResponse<any>>(AuthEndpoints.Logout,
        { id: currentUser?.refId, userTypeId: currentUser?.userTypeId }
      )
      .pipe(
        map((res) => {
          this.currentUserService.logout();
          return res;
        }),
        catchError(() => {
          // Always clear local session even if the API call fails (e.g. CORS / network error)
          this.currentUserService.logout();
          return of(null as any);
        })
      );
  }

  forgotPassword(email: string): Observable<HttpResponse<ApiResponse<any>>> {
    return this.httpClient.post<ApiResponse<any>>(
      `${this.appConfig.apiEndpoint}${AuthEndpoints.ForgotPassword}?emailId=${email}`,
      null,
      { observe: 'response' }
    );
  }

  resetPassword(resetPasswordModel: IResetPasswordModel): Observable<HttpResponse<ApiResponse<any>>> {
    return this.httpClient.post<ApiResponse<any>>(
      `${this.appConfig.apiEndpoint}${AuthEndpoints.ResetPassword}`,
      resetPasswordModel,
      { observe: 'response' }
    );
  }

  changePassword(changePasswordModel: IChangePasswordModel): Observable<ApiResponse<any>> {
    return this.httpWithInterceptors.post<ApiResponse<any>>(
      AuthEndpoints.ChangePassword,
      changePasswordModel
    );
  }

  firstTimeLoginRequest(email: string): Observable<HttpResponse<ApiResponse<any>>> {
    return this.httpClient.post<ApiResponse<any>>(
      `${this.appConfig.apiEndpoint}${AuthEndpoints.FirstTimeLoginRequest}`,
      { email },
      { observe: 'response' }
    );
  }

  firstTimeLoginVerify(patientIdEnc: string, otp: string): Observable<HttpResponse<ApiResponse<any>>> {
    return this.httpClient.post<ApiResponse<any>>(
      `${this.appConfig.apiEndpoint}${AuthEndpoints.FirstTimeLoginVerify}`,
      { patientIdEnc, otp },
      { observe: 'response' }
    );
  }

  firstTimeLoginSetPassword(payload: any): Observable<HttpResponse<ApiResponse<any>>> {
    return this.httpClient.post<ApiResponse<any>>(
      `${this.appConfig.apiEndpoint}${AuthEndpoints.FirstTimeLoginSetPassword}`,
      payload,
      { observe: 'response' }
    );
  }

  isAuthenticated(): boolean {
    const currentUser = this.currentUserService.currentUser();
    if (!currentUser?.jwtToken) return false;
    return new Date() <= new Date(currentUser.expiration);
  }

  isUnAuthenticated(): boolean {
    return !this.isAuthenticated();
  }

  hasRefreshToken(): boolean {
    const currentUser = this.currentUserService.currentUser();
    return !!(currentUser && currentUser.refreshToken);
  }

  refreshToken(): Observable<HttpResponse<ApiResponse<ILoginResponseModel>>> {
    const currentUser = this.currentUserService.currentUser();
    const token = currentUser?.refreshToken;
    return this.httpClient.post<ApiResponse<ILoginResponseModel>>(
      `${this.appConfig.apiEndpoint}/patients/refreshtoken`,
      { refreshToken: token },
      { observe: 'response' }
    );
  }
}